/**
 * L'effort sur une période — le détail que le tableau de bord ne montre pas.
 *
 * Demandé par Hedi le 30 août 2026 : *« un autre écran qui détaille sur une
 * période donnée, le nombre d'exercices passés, temps passé, nombre d'exercices
 * passés en fonction du nombre d'indices donnés, nombre de fausses réponses
 * avant résolution »*.
 *
 * **C'est un écran d'effort, pas un écran de niveau**, et c'est la raison pour
 * laquelle il ne vit pas dans la fiche de l'enfant. `progression` répond à
 * « où en est-elle ? » ; celui-ci répond à « qu'est-ce qu'elle a fait ? ». Les
 * mélanger ferait lire « huit exercices à trois indices » comme un bulletin,
 * alors que c'est le portrait d'un enfant qui a insisté.
 *
 * Deux règles de dessin, héritées du reste de l'application :
 *
 * - **Aucune couleur ne juge.** Les quatre barres d'un histogramme portent la
 *   même teinte. Un dégradé qui fonce quand les indices augmentent dirait « plus
 *   sombre, donc plus grave » sans que personne l'ait décidé, et le contrat
 *   interdit de mettre l'aide en accusation : demander trois indices et finir
 *   par trouver **est** du travail.
 * - **Le chiffre est écrit à côté de la barre.** Quatre barres, quatre nombres :
 *   à cette taille, les valeurs écrites remplacent l'axe au lieu de le doubler,
 *   et la liste reste lisible sans voir les barres du tout.
 */

import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import { call } from "@/api/client";
import { ApiError } from "@/api/errors";
import type { EffortReport } from "@/api/schema";
import {
  assiseDuTemps,
  bornes,
  duree,
  enClair,
  jour,
  largeur,
  resolution,
  seauLibelle,
  type Periode,
  type Preselection,
  type Seau,
} from "@/parent/effort";
import { SelecteurDePeriode } from "@/ui/SelecteurDePeriode";
import { Card, Empty, ErrorNote, Loading, Screen } from "@/ui/primitives";
import { etiquette } from "@/ui/matieres";

export function EffortScreen() {
  const { childId = "" } = useParams();
  const [choix, setChoix] = useState<Preselection>("30j");
  const aujourdhui = useMemo(() => new Date(), []);
  const [periode, setPeriode] = useState<Periode>(
    () => bornes("30j", new Date()) ?? { since: "", until: "" },
  );
  const [rapport, setRapport] = useState<EffortReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!periode.since || !periode.until) return;
    let annule = false;
    setError(null);
    // Le rapport précédent reste à l'écran pendant le chargement du suivant :
    // vider la page à chaque changement de période la fait clignoter, et un
    // parent qui compare deux périodes perd le chiffre qu'il avait sous les yeux.
    void (async () => {
      try {
        const charge = await call("effort", {
          params: { child_id: childId },
          query: { since: periode.since, until: periode.until },
        });
        if (!annule) setRapport(charge);
      } catch (echec) {
        if (!annule) {
          setError(echec instanceof ApiError ? echec.message : "Chargement impossible.");
        }
      }
    })();
    return () => {
      annule = true;
    };
  }, [childId, periode.since, periode.until]);

  const choisir = (valeur: Preselection) => {
    setChoix(valeur);
    const calcule = bornes(valeur, aujourdhui);
    if (calcule) setPeriode(calcule);
  };

  return (
    <Screen
      title="Le travail fourni"
      lead={
        periode.since && periode.until
          ? `Du ${enClair(periode.since)} au ${enClair(periode.until)}`
          : undefined
      }
    >
      <SelecteurDePeriode
        choix={choix}
        periode={periode}
        max={jour(aujourdhui)}
        onChoisir={choisir}
        onDates={(suivante) => {
          setChoix("libre");
          setPeriode(suivante);
        }}
      />

      {error ? <ErrorNote>{error}</ErrorNote> : null}
      {!rapport && !error ? <Loading /> : null}
      {rapport ? <Rapport rapport={rapport} /> : null}
    </Screen>
  );
}

function Rapport({ rapport }: { rapport: EffortReport }) {
  const exercices = rapport.exercises ?? 0;

  if (exercices === 0) {
    return (
      <Card>
        <Empty>Aucun exercice rendu sur cette période.</Empty>
      </Card>
    );
  }

  const temps = duree(rapport.duration_s);
  const assise = assiseDuTemps(rapport.timed_exercises ?? 0, exercices);
  const jours = rapport.days_worked ?? 0;

  return (
    <>
      <Card title="En résumé">
        <ul className="chiffres">
          <Chiffre valeur={String(exercices)} quoi="exercices rendus" />
          <Chiffre
            valeur={temps ?? "—"}
            quoi="de travail"
            precision={temps ? (assise ?? undefined) : "aucune durée mesurée"}
          />
          <Chiffre valeur={String(jours)} quoi={jours > 1 ? "jours travaillés" : "jour travaillé"} />
          <Chiffre
            valeur={String(rapport.attempts ?? 0)}
            quoi="réponses envoyées"
            precision={resolution(rapport.solved ?? 0, exercices) ?? undefined}
          />
        </ul>
        {rapport.unreadable ? (
          <p className="muted small">
            {rapport.unreadable === 1
              ? "1 réponse n'a pas pu être lue par le système"
              : `${rapport.unreadable} réponses n'ont pas pu être lues par le système`}{" "}
            — elles ne comptent nulle part comme des erreurs.
          </p>
        ) : null}
      </Card>

      <Histogramme
        titre="Exercices selon le nombre d'indices"
        aide="Trois indices existent pour chaque exercice. En demander est prévu, pas subi : l'échelle sert à ne jamais rester bloqué."
        seaux={rapport.by_hints ?? []}
        unite={(n) => (n === 0 ? "Aucun indice" : n === 1 ? "1 indice" : `${n} indices`)}
      />

      <Histogramme
        titre="Exercices selon le nombre de réponses fausses avant résolution"
        aide="Comptées avant la première réussite seulement. Un exercice jamais résolu compte toutes ses réponses fausses."
        seaux={rapport.by_wrong ?? []}
        unite={(n) =>
          n === 0 ? "Juste du premier coup" : n === 1 ? "1 réponse fausse" : `${n} réponses fausses`
        }
      />

      <Card title="Par matière">
        <ul className="work-list">
          {(rapport.by_subject ?? []).map((matiere) => (
            <li key={matiere.subject} className="work">
              <div className="work__head">
                <span className="work__label">{etiquette(matiere.subject)}</span>
                <span className="work__time">{duree(matiere.duration_s) ?? "—"}</span>
              </div>
              <p className="muted small">
                {matiere.exercises} exercice{matiere.exercises > 1 ? "s" : ""} ·{" "}
                {resolution(matiere.solved, matiere.exercises)} · {matiere.attempts} réponse
                {matiere.attempts > 1 ? "s" : ""} envoyée{matiere.attempts > 1 ? "s" : ""}
              </p>
            </li>
          ))}
        </ul>
      </Card>
    </>
  );
}

/** Une tuile de chiffre : la valeur d'abord, ce qu'elle compte ensuite. */
function Chiffre({
  valeur,
  quoi,
  precision,
}: {
  valeur: string;
  quoi: string;
  precision?: string;
}) {
  return (
    <li className="chiffre">
      <span className="chiffre__valeur">{valeur}</span>
      <span className="chiffre__quoi">{quoi}</span>
      {precision ? <span className="chiffre__precision">{precision}</span> : null}
    </li>
  );
}

function Histogramme({
  titre,
  aide,
  seaux,
  unite,
}: {
  titre: string;
  aide: string;
  seaux: Seau[];
  unite: (n: number) => string;
}) {
  if (seaux.every((seau) => seau.exercises === 0)) return null;

  return (
    <Card title={titre}>
      <p className="muted small">{aide}</p>
      {/* Une liste de définitions : le libellé et son nombre restent appariés
          pour un lecteur d'écran, et la barre n'est que la mise en image d'un
          chiffre déjà écrit — elle porte donc `aria-hidden`. */}
      <dl className="histo">
        {seaux.map((seau) => (
          <div key={seau.key} className="histo__ligne">
            <dt className="histo__libelle">{seauLibelle(seau, unite)}</dt>
            <dd className="histo__valeur">
              <span className="histo__piste" aria-hidden="true">
                <span
                  className="histo__barre"
                  style={{ width: `${largeur(seau, seaux)}%` }}
                />
              </span>
              <span className="histo__nombre">{seau.exercises}</span>
            </dd>
          </div>
        ))}
      </dl>
    </Card>
  );
}
